/**
 * @author funfly
 * @mail 389193@qq.com
 * @date 2025-11-13
 * @copyright Gridy.Art
 */
import conf from '@/js/conf/conf'
import download from 'downloadjs'
import JSZip from 'jszip'
import i18n from '../../lang'
import * as THREE from 'three'
// eslint-disable-next-line
const $t = (key, value) => i18n.t(key, value)
const cdnHost = conf.hosts().cdnHost
const templatePath = cdnHost + 'template.3mf?' + conf.ver

// GLB 文件格式常量
const GLB_CONSTANTS = {
  MAGIC: 0x46546C67, // "glTF" 魔数
  VERSION: 2, // 支持的 GLB 版本
  HEADER_SIZE: 12, // GLB 头部大小（字节）
  CHUNK_JSON: 0x4E4F534A, // "JSON" chunk 类型
  CHUNK_BIN: 0x004E4942 // "BIN\0" chunk 类型
}

// glTF 访问器组件类型常量
const GLTF_COMPONENT_TYPES = {
  BYTE: 5120, // 8 位有符号整数
  UNSIGNED_BYTE: 5121, // 8 位无符号整数
  SHORT: 5122, // 16 位有符号整数
  UNSIGNED_SHORT: 5123, // 16 位无符号整数
  UNSIGNED_INT: 5125, // 32 位无符号整数
  FLOAT: 5126 // 32 位浮点数
}

// 3D 打印模型尺寸限制常量
const MODEL_CONSTRAINTS = {
  MIN_DIMENSION: 60, // 最小尺寸（毫米）
  MAX_DIMENSION: 200 // 最大尺寸（毫米）
}

const main = {
  /**
   * 将 Three.js GLTFLoader 加载的对象转换为 3MF 格式的压缩包文件并下载
   * @param {Object} gltfObj - Three.js GLTFLoader 返回的对象（包含 scene 属性）
   * @param {Function} cb - 回调函数 (error, fileName)
   * @param {number} scale - 缩放比例（可选，默认 0 表示自动计算）
   */
  run: async function(gltfObj, cb, name = '', scale = 0) {
    try {
      if (!gltfObj) {
        return cb && cb($t('i.actFail'), null)
      }
      // 从 Three.js 场景中提取网格数据
      const meshData = this.extractMeshDataFromScene(gltfObj)

      if (!meshData.vertices || meshData.vertices.length === 0) {
        return cb && cb($t('i.actFail'), null)
      }
      // 计算缩放
      const scaleDt = this.calculateScale(meshData.vertices, scale)
      // 应用缩放、翻转和旋转
      // 1. 先应用缩放
      // 2. 绕 X 轴顺时针旋转 90 度（让模型站起来）
      // 3. 翻转 Z 轴以修正上下颠倒
      // 4. 绕 Z 轴水平旋转 180 度（使正面朝前）
      const scaledVertices = meshData.vertices.map(v => {
        // 应用缩放
        const x = v.x * scaleDt.scale
        const y = v.y * scaleDt.scale
        const z = v.z * scaleDt.scale

        // 绕 X 轴顺时针旋转 90 度（让模型站起来）
        // 旋转矩阵: [1   0    0 ]   [x]   [x ]
        //          [0   0    1 ] × [y] = [z ]
        //          [0  -1    0 ]   [z]   [-y]
        const xRotated = x
        const yRotated = z
        const zRotated = y

        // 绕 Z 轴旋转 180 度（水平旋转，使正面朝前）
        // 旋转矩阵: [cos(180) -sin(180)  0]   [-1  0  0]
        //          [sin(180)  cos(180)  0] = [0  -1  0]
        //          [0          0        1]   [0   0  1]
        return {
          x: -xRotated, // x → -x
          y: -yRotated, // y → -z
          z: zRotated // z → y
        }
      })

      // 加载模板文件
      const templateBlob = await fetch(templatePath).then(res => {
        if (!res.ok) {
          cb && cb($t('i.actFail'), null)
          return
        }
        return res.blob()
      })
      const templateZip = await JSZip.loadAsync(templateBlob)

      // 从 zip 包中读取并解析项目配置文件
      let jsonConf = {}
      const configFile = templateZip.file('Metadata/project_settings.config')
      if (configFile) {
        const configContent = await configFile.async('string')
        if (configContent && configContent.trim()) {
          jsonConf = JSON.parse(configContent)
        }
      }

      // 创建项目配置（使用默认白色，因为 GLB 可能没有颜色信息）
      const defaultColor = '#FFFFFF'
      const projectConfig = this.createProjectConfig(jsonConf, [defaultColor])
      templateZip.file('Metadata/project_settings.config', JSON.stringify(projectConfig, null, 4))

      // 生成 3MF XML（不带颜色，因为 GLB 通常没有颜色信息）
      const triangles = meshData.triangles.map(t => ({
        v1: t.v1,
        v2: t.v2,
        v3: t.v3
        // 不添加 paint_color，使用默认白色
      }))
      const xml = this.generate3mfXml(scaledVertices, triangles)
      templateZip.file('3D/Objects/object_14.model', xml)

      // 生成最终的 3MF 文件
      const finalBlob = await templateZip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 9 }
      })

      // 使用 download 下载文件
      const fileName = (name ? name + '.' : '') + 'Gridy.Art.3mf'
      download(finalBlob, fileName, 'application/vnd.ms-package.3dmanufacturing-3dmodel+xml')
      cb && cb(null, fileName)
    } catch (error) {
      cb && cb(error, null)
    }
  },

  /**
   * 解析 GLB 缓冲区，提取 JSON 和二进制数据
   * @param {ArrayBuffer|Blob} input - GLB 文件的二进制缓冲区或 Blob
   * @returns {{jsonChunk: Object, binaryChunk: ArrayBuffer}} 解析后的数据
   */
  parseGltfBuffer: async function(input) {
    // 如果输入是 Blob，先转换为 ArrayBuffer
    let arrayBuffer
    if (input instanceof Blob) {
      arrayBuffer = await input.arrayBuffer()
    } else if (input instanceof ArrayBuffer) {
      arrayBuffer = input
    } else {
      throw new Error('无效的输入类型：必须是 Blob 或 ArrayBuffer')
    }

    if (!arrayBuffer || arrayBuffer.byteLength < GLB_CONSTANTS.HEADER_SIZE) {
      throw new Error('无效的 GLB 文件：文件太小')
    }

    const dataView = new DataView(arrayBuffer)
    const magic = dataView.getUint32(0, true)
    const version = dataView.getUint32(4, true)
    const totalLength = dataView.getUint32(8, true)

    if (magic !== GLB_CONSTANTS.MAGIC) {
      throw new Error('无效的 GLB 文件：magic number 不匹配')
    }

    if (version !== GLB_CONSTANTS.VERSION) {
      throw new Error(`不支持的 GLB 版本：${version}，仅支持版本 ${GLB_CONSTANTS.VERSION}`)
    }

    let offset = GLB_CONSTANTS.HEADER_SIZE
    let jsonChunk = null
    let binaryChunk = null

    while (offset < totalLength) {
      const chunkLength = dataView.getUint32(offset, true)
      const chunkType = dataView.getUint32(offset + 4, true)
      const chunkData = arrayBuffer.slice(offset + 8, offset + 8 + chunkLength)

      if (chunkType === GLB_CONSTANTS.CHUNK_JSON) { // "JSON"
        const decoder = new TextDecoder('utf-8')
        jsonChunk = JSON.parse(decoder.decode(chunkData))
      } else if (chunkType === GLB_CONSTANTS.CHUNK_BIN) { // "BIN\0"
        binaryChunk = chunkData
      }

      offset += 8 + chunkLength
    }

    if (!jsonChunk) {
      throw new Error('GLB 文件缺少 JSON chunk')
    }

    if (!binaryChunk) {
      throw new Error('GLB 文件缺少 binary chunk')
    }

    return { jsonChunk, binaryChunk }
  },

  /**
   * 从 Three.js 场景中提取网格数据（顶点和三角形）
   * @param {THREE.Scene|THREE.Object3D} scene - Three.js 场景或对象
   * @returns {{vertices: Array, triangles: Array}} 网格数据对象
   */
  extractMeshDataFromScene: function(scene) {
    const vertices = []
    const triangles = []
    const vertexIndexMap = new Map() // 用于去重顶点

    if (!scene) {
      return { vertices, triangles }
    }

    // 获取或创建顶点索引（去重）
    const getVertexIndex = (x, y, z) => {
      const key = `${x.toFixed(6)},${y.toFixed(6)},${z.toFixed(6)}`
      if (vertexIndexMap.has(key)) {
        return vertexIndexMap.get(key)
      }
      const idx = vertices.length
      vertices.push({ x, y, z })
      vertexIndexMap.set(key, idx)
      return idx
    }

    // 遍历场景中的所有 Mesh 对象
    scene.traverse((object) => {
      // 只处理 Mesh 对象
      if (!object.isMesh || !object.geometry) {
        return
      }

      const geometry = object.geometry
      const positionAttribute = geometry.attributes.position

      if (!positionAttribute) {
        return
      }

      // 获取世界变换矩阵
      object.updateMatrixWorld()
      const matrixWorld = object.matrixWorld

      // 读取顶点位置
      const positions = positionAttribute.array
      const positionCount = positionAttribute.count

      // 应用世界变换并获取顶点索引
      const localVertexIndices = []
      const tempVector = new THREE.Vector3() // Use Three.js Vector3 for transformations
      for (let i = 0; i < positionCount; i++) {
        tempVector.set(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2])
        tempVector.applyMatrix4(matrixWorld)

        // 获取或创建顶点索引
        const vertexIndex = getVertexIndex(tempVector.x, tempVector.y, tempVector.z)
        localVertexIndices.push(vertexIndex)
      }

      // 读取索引数据
      let indices = null
      if (geometry.index) {
        // 如果有索引，使用索引
        const indexAttribute = geometry.index
        const indexArray = indexAttribute.array
        indices = []
        for (let i = 0; i < indexAttribute.count; i++) {
          if (indexAttribute.array instanceof Uint16Array || indexAttribute.array instanceof Int16Array) {
            indices.push(localVertexIndices[indexArray[i]])
          } else if (indexAttribute.array instanceof Uint32Array || indexAttribute.array instanceof Int32Array) {
            indices.push(localVertexIndices[indexArray[i]])
          } else {
            indices.push(localVertexIndices[indexArray[i]])
          }
        }
      } else {
        // 如果没有索引，使用顺序索引
        indices = localVertexIndices
      }

      // 处理三角形
      for (let i = 0; i < indices.length; i += 3) {
        if (i + 2 >= indices.length) break

        const i1 = indices[i]
        const i2 = indices[i + 1]
        const i3 = indices[i + 2]

        // 确保索引有效
        if (i1 !== undefined && i2 !== undefined && i3 !== undefined) {
          triangles.push({ v1: i1, v2: i2, v3: i3 })
        }
      }
    })

    return { vertices, triangles }
  },

  /**
   * 从 glTF 数据中提取网格数据（顶点和三角形）
   * @param {Object} gltf - glTF JSON 对象
   * @param {ArrayBuffer} binaryBuffer - 二进制数据缓冲区
   * @returns {{vertices: Array, triangles: Array}} 网格数据对象
   */
  extractMeshData: function(gltf, binaryBuffer) {
    const vertices = []
    const triangles = []
    const vertexIndexMap = new Map() // 用于去重顶点

    if (!gltf.meshes || gltf.meshes.length === 0) {
      return { vertices, triangles }
    }

    const accessors = gltf.accessors || []
    const bufferViews = gltf.bufferViews || []

    for (let meshIndex = 0; meshIndex < gltf.meshes.length; meshIndex++) {
      const mesh = gltf.meshes[meshIndex]

      if (mesh.primitives && mesh.primitives.length > 0) {
        for (let primIndex = 0; primIndex < mesh.primitives.length; primIndex++) {
          const primitive = mesh.primitives[primIndex]

          // 只处理三角形
          if (primitive.mode !== undefined && primitive.mode !== 4) {
            continue
          }

          // 读取顶点位置
          const positionAccessorIndex = primitive.attributes.POSITION
          if (positionAccessorIndex === undefined) continue

          const positionAccessor = accessors[positionAccessorIndex]
          if (!positionAccessor || positionAccessor.bufferView === undefined) continue

          const positionBufferView = bufferViews[positionAccessor.bufferView]
          if (!positionBufferView) continue

          const positionData = this.readAccessorData(
            binaryBuffer,
            positionBufferView,
            positionAccessor,
            'float',
            3
          )

          // 读取索引
          let indices = null
          if (primitive.indices !== undefined) {
            const indexAccessor = accessors[primitive.indices]
            if (indexAccessor && indexAccessor.bufferView !== undefined) {
              const indexBufferView = bufferViews[indexAccessor.bufferView]
              if (indexBufferView) {
                // 根据 componentType 确定索引数据类型
                let indexType = 'ushort'
                if (indexAccessor.componentType === GLTF_COMPONENT_TYPES.UNSIGNED_INT) {
                  indexType = 'uint'
                } else if (indexAccessor.componentType === GLTF_COMPONENT_TYPES.UNSIGNED_SHORT) {
                  indexType = 'ushort'
                } else if (indexAccessor.componentType === GLTF_COMPONENT_TYPES.UNSIGNED_BYTE) {
                  indexType = 'ubyte'
                }

                const indexData = this.readAccessorData(
                  binaryBuffer,
                  indexBufferView,
                  indexAccessor,
                  indexType,
                  1
                )
                indices = indexData.map(item => item[0])
              }
            }
          }

          if (!indices) {
            // 如果没有索引，创建顺序索引
            indices = []
            for (let i = 0; i < positionData.length; i++) {
              indices.push(i)
            }
          }

          // 获取或创建顶点索引（去重）
          const getVertexIndex = (x, y, z) => {
            const key = `${x.toFixed(6)},${y.toFixed(6)},${z.toFixed(6)}`
            if (vertexIndexMap.has(key)) {
              return vertexIndexMap.get(key)
            }
            const idx = vertices.length
            vertices.push({ x, y, z })
            vertexIndexMap.set(key, idx)
            return idx
          }

          // 处理三角形
          for (let i = 0; i < indices.length; i += 3) {
            if (i + 2 >= indices.length) break

            const idx1 = indices[i]
            const idx2 = indices[i + 1]
            const idx3 = indices[i + 2]

            const v1 = positionData[idx1]
            const v2 = positionData[idx2]
            const v3 = positionData[idx3]

            const i1 = getVertexIndex(v1[0], v1[1], v1[2])
            const i2 = getVertexIndex(v2[0], v2[1], v2[2])
            const i3 = getVertexIndex(v3[0], v3[1], v3[2])

            triangles.push({ v1: i1, v2: i2, v3: i3 })
          }
        }
      }
    }

    return { vertices, triangles }
  },

  /**
   * 从缓冲区读取访问器数据
   * @param {ArrayBuffer} binaryBuffer - 二进制数据缓冲区
   * @param {Object} bufferView - bufferView 对象
   * @param {Object} accessor - accessor 对象
   * @param {string} type - 数据类型 ('float', 'ushort', 'uint', 'ubyte')
   * @param {number} components - 组件数量（每个元素的组件数，如 VEC3 = 3）
   * @returns {Array<Array<number>>} 读取的数据数组
   */
  readAccessorData: function(binaryBuffer, bufferView, accessor, type, components) {
    // 确保 binaryBuffer 是 ArrayBuffer
    if (!(binaryBuffer instanceof ArrayBuffer)) {
      throw new Error('binaryBuffer 必须是 ArrayBuffer 类型')
    }

    const dataView = new DataView(binaryBuffer)
    const baseByteOffset = bufferView.byteOffset || 0
    const accessorByteOffset = accessor.byteOffset || 0
    const byteOffset = baseByteOffset + accessorByteOffset

    // 计算每个组件的字节大小
    let componentSize = 4 // 默认 float
    if (type === 'ushort') {
      componentSize = 2
    } else if (type === 'uint') {
      componentSize = 4
    } else if (type === 'ubyte') {
      componentSize = 1
    } else if (type === 'float') {
      componentSize = 4
    }

    // 计算步长（stride），如果 bufferView 有 byteStride 则使用它，否则使用组件大小
    const byteStride = bufferView.byteStride || (components * componentSize)

    const result = []

    for (let i = 0; i < accessor.count; i++) {
      const values = []
      const elementOffset = byteOffset + (i * byteStride)

      for (let j = 0; j < components; j++) {
        const componentOffset = elementOffset + (j * componentSize)
        let value

        if (type === 'float') {
          value = dataView.getFloat32(componentOffset, true)
        } else if (type === 'ushort') {
          value = dataView.getUint16(componentOffset, true)
        } else if (type === 'uint') {
          value = dataView.getUint32(componentOffset, true)
        } else if (type === 'ubyte') {
          value = dataView.getUint8(componentOffset)
        } else {
          throw new Error(`不支持的数据类型: ${type}`)
        }

        values.push(value)
      }

      result.push(values)
    }

    return result
  },

  /**
   * 计算模型缩放比例
   * @param {Array<{x: number, y: number, z: number}>} vertices - 顶点数组
   * @param {number} scale - 缩放比例（0 表示自动计算）
   * @returns {scale: number} 缩放比例
   */
  calculateScale: function(vertices, scale) {
    if (vertices.length === 0) {
      return { scale: 1 }
    }
    // 计算边界框
    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity
    let minZ = Infinity
    let maxZ = -Infinity

    for (const v of vertices) {
      minX = Math.min(minX, v.x)
      maxX = Math.max(maxX, v.x)
      minY = Math.min(minY, v.y)
      maxY = Math.max(maxY, v.y)
      minZ = Math.min(minZ, v.z)
      maxZ = Math.max(maxZ, v.z)
    }

    const sizeX = maxX - minX
    const sizeY = maxY - minY
    const sizeZ = maxZ - minZ
    const sizeMax = Math.max(sizeX, sizeY, sizeZ)
    const sizeMin = Math.min(sizeX, sizeY, sizeZ)
    if (scale === 0) {
      if (sizeMax > MODEL_CONSTRAINTS.MAX_DIMENSION) {
        scale = MODEL_CONSTRAINTS.MAX_DIMENSION / sizeMax
      }
      if (sizeMax < MODEL_CONSTRAINTS.MIN_DIMENSION) {
        scale = MODEL_CONSTRAINTS.MIN_DIMENSION / sizeMax
      }
    }
    return { sizeX, sizeY, sizeZ, sizeMin, sizeMax, scale }
  },

  /**
   * 生成 3MF 格式的 XML 文件内容
   * @param {Array<{x: number, y: number, z: number}>} vertices - 顶点数组
   * @param {Array<{v1: number, v2: number, v3: number}>} triangles - 三角形数组
   * @returns {string} 3MF XML 字符串
   */
  generate3mfXml: function(vertices, triangles) {
    const header = `<?xml version="1.0" encoding="UTF-8"?>\n` +
      `<model unit="millimeter" xml:lang="en-US" xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02" xmlns:BambuStudio="http://schemas.bambulab.com/package/2021" xmlns:p="http://schemas.microsoft.com/3dmanufacturing/production/2015/06" requiredextensions="p">\n` +
      ` <metadata name="BambuStudio:3mfVersion">1</metadata>\n` +
      ` <resources>\n` +
      `  <object id="1" p:UUID="000e0000-81cb-4c03-9d28-80fed5dfa1dc" type="model">\n` +
      `   <mesh>\n` +
      `    <vertices>\n`

    let verticesXml = ''
    for (let i = 0; i < vertices.length; i++) {
      const v = vertices[i]
      verticesXml += `     <vertex x="${v.x}" y="${v.y}" z="${v.z}"/>\n`
    }

    let trianglesXml = ''
    for (let i = 0; i < triangles.length; i++) {
      const t = triangles[i]
      // GLB 通常没有颜色信息，不添加 paint_color 属性
      trianglesXml += `     <triangle v1="${t.v1}" v2="${t.v2}" v3="${t.v3}"/>\n`
    }

    const tail = verticesXml + `    </vertices>\n` +
      `    <triangles>\n` +
      trianglesXml +
      `    </triangles>\n` +
      `   </mesh>\n` +
      `  </object>\n` +
      ` </resources>\n` +
      ` <build>\n` +
      `  <item objectid="1"/>\n` +
      ` </build>\n` +
      `</model>\n`

    return header + tail
  },

  /**
   * 创建 3MF 项目配置文件
   * @param {Object} jsonConf - 从模板中读取的现有配置（如果存在）
   * @param {Array<string>} paletteArr - 使用的颜色数组（十六进制格式）
   * @returns {Object} 更新后的项目配置
   */
  createProjectConfig: function(jsonConf, paletteArr) {
    // 如果 jsonConf 为空或不是对象，初始化为空对象
    if (!jsonConf || typeof jsonConf !== 'object') {
      jsonConf = {}
    }
    const colorNums = paletteArr.length
    const defaultValue = {
      'activate_air_filtration': '0',
      'additional_cooling_fan_speed': '70',
      'chamber_temperatures': '0',
      'circle_compensation_speed': '200',
      'close_fan_the_first_x_layers': '1',
      'complete_print_exhaust_fan_speed': '70',
      'cool_plate_temp': '35',
      'cool_plate_temp_initial_layer': '35',
      'counter_coef_1': '0',
      'counter_coef_2': '0.008',
      'counter_coef_3': '-0.041',
      'counter_limit_max': '0.033',
      'counter_limit_min': '-0.035',
      'default_filament_colour': '',
      'diameter_limit': '50',
      'different_settings_to_system': '',
      'during_print_exhaust_fan_speed': '70',
      'enable_overhang_bridge_fan': '1',
      'enable_pressure_advance': '0',
      'eng_plate_temp': '0',
      'eng_plate_temp_initial_layer': '0',
      'fan_cooling_layer_time': '100',
      'fan_max_speed': '100',
      'fan_min_speed': '100',
      'filament_adaptive_volumetric_speed': ['0', '0'],
      'filament_adhesiveness_category': '100',
      'filament_change_length': '5',
      'filament_colour_type': '1',
      'filament_cost': '24.99',
      'filament_density': '1.26',
      'filament_deretraction_speed': ['nil', 'nil'],
      'filament_diameter': '1.75',
      'filament_end_gcode': '; filament end gcode \\n\\n',
      'filament_extruder_variant': ['Direct Drive Standard', 'Direct Drive High Flow'],
      'filament_flow_ratio': ['0.98', '0.98'],
      'filament_flush_temp': ['0', '0'],
      'filament_flush_volumetric_speed': ['0', '0'],
      'filament_ids': 'GFA00',
      'filament_is_support': '0',
      'filament_long_retractions_when_cut': ['1', '1'],
      'filament_map': '1',
      'filament_max_volumetric_speed': ['21', '21'],
      'filament_minimal_purge_on_wipe_tower': '15',
      'filament_pre_cooling_temperature': ['0', '0'],
      'filament_prime_volume': '30',
      'filament_printable': '3',
      'filament_ramming_travel_time': ['0', '0'],
      'filament_ramming_volumetric_speed': ['-1', '-1'],
      'filament_retract_before_wipe': ['nil', 'nil'],
      'filament_retract_restart_extra': ['nil', 'nil'],
      'filament_retract_when_changing_layer': ['nil', 'nil'],
      'filament_retraction_distances_when_cut': ['18', '18'],
      'filament_retraction_length': ['nil', 'nil'],
      'filament_retraction_minimum_travel': ['nil', 'nil'],
      'filament_retraction_speed': ['nil', 'nil'],
      'filament_scarf_gap': '0%',
      'filament_scarf_height': '10%',
      'filament_scarf_length': '10',
      'filament_scarf_seam_type': 'none',
      'filament_settings_id': 'Bambu PLA Basic @BBL X1C',
      'filament_shrink': '100%',
      'filament_soluble': '0',
      'filament_start_gcode': '; filament start gcode\\n{if  (bed_temperature[current_extruder] >55)||(bed_temperature_initial_layer[current_extruder] >55)}M106 P3 S200\\n{elsif(bed_temperature[current_extruder] >50)||(bed_temperature_initial_layer[current_extruder] >50)}M106 P3 S150\\n{elsif(bed_temperature[current_extruder] >45)||(bed_temperature_initial_layer[current_extruder] >45)}M106 P3 S50\\n{endif}\\nM142 P1 R35 S40\\n{if activate_air_filtration[current_extruder] && support_air_filtration}\\nM106 P3 S{during_print_exhaust_fan_speed_num[current_extruder]} \\n{endif}',
      'filament_type': 'PLA',
      'filament_velocity_adaptation_factor': '1',
      'filament_vendor': 'Bambu Lab',
      'filament_wipe': ['nil', 'nil'],
      'filament_wipe_distance': ['nil', 'nil'],
      'filament_z_hop': ['nil', 'nil'],
      'filament_z_hop_types': ['nil', 'nil'],
      'first_x_layer_fan_speed': '0',
      'flush_volumes_matrix': ['0', '0'],
      'flush_volumes_vector': ['140', '140'],
      'full_fan_speed_layer': '0',
      'hole_coef_1': '0',
      'hole_coef_2': '-0.008',
      'hole_coef_3': '0.23415',
      'hole_limit_max': '0.22',
      'hole_limit_min': '0.088',
      'hot_plate_temp': '55',
      'hot_plate_temp_initial_layer': '55',
      'impact_strength_z': '13.8',
      'long_retractions_when_ec': ['0', '0'],
      'no_slow_down_for_cooling_on_outwalls': '0',
      'nozzle_temperature': ['220', '220'],
      'nozzle_temperature_initial_layer': ['220', '220'],
      'nozzle_temperature_range_high': '240',
      'nozzle_temperature_range_low': '190',
      'overhang_fan_speed': '100',
      'overhang_fan_threshold': '50%',
      'overhang_threshold_participating_cooling': '95%',
      'pre_start_fan_time': '0',
      'pressure_advance': '0.02',
      'reduce_fan_stop_start_freq': '1',
      'required_nozzle_HRC': '3',
      'retraction_distances_when_ec': ['0', '0'],
      'slow_down_for_layer_cooling': '1',
      'slow_down_layer_time': '4',
      'slow_down_min_speed': '20',
      'supertack_plate_temp': '45',
      'supertack_plate_temp_initial_layer': '45',
      'temperature_vitrification': '45',
      'textured_plate_temp': '55',
      'textured_plate_temp_initial_layer': '55',
      'volumetric_speed_coefficients': ['0 0 0 0 0 0', '0 0 0 0 0 0']
    }

    // 根据 defaultValue 生成/覆盖对应 key 的数组值
    const buildArray = function(val, len) {
      if (Array.isArray(val)) {
        const out = new Array(len)
        const base = val.length > 0 ? val : ['']
        for (let i = 0; i < len; i++) out[i] = base[i % base.length]
        return out
      }
      const out = new Array(len)
      for (let i = 0; i < len; i++) out[i] = val
      return out
    }

    // 更新配置：如果 jsonConf 中已有该 key，保留原有值；否则使用默认值
    Object.keys(defaultValue).forEach((key) => {
      const defVal = defaultValue[key]
      const isArray = Array.isArray(defVal)
      const targetLen = isArray ? colorNums * defVal.length : colorNums

      // 如果 jsonConf 中已有该 key，检查是否需要扩展数组长度
      if (Object.prototype.hasOwnProperty.call(jsonConf, key)) {
        const existingVal = jsonConf[key]
        if (Array.isArray(existingVal)) {
          // 如果现有值是数组，检查长度是否匹配
          if (existingVal.length !== targetLen) {
            // 长度不匹配，使用默认值重新构建
            jsonConf[key] = buildArray(defVal, targetLen)
          }
          // 如果长度匹配，保留原有值
        } else {
          // 如果现有值不是数组，但默认值需要数组，则使用默认值构建
          if (isArray || targetLen > 1) {
            jsonConf[key] = buildArray(defVal, targetLen)
          }
          // 如果都是标量，保留原有值
        }
      } else {
        // 如果 jsonConf 中没有该 key，使用默认值构建
        jsonConf[key] = buildArray(defVal, targetLen)
      }
    })

    jsonConf.filament_colour = paletteArr
    jsonConf.filament_multi_colour = paletteArr
    jsonConf.different_settings_to_system = jsonConf.different_settings_to_system || []
    jsonConf.different_settings_to_system.unshift('enable_support')

    const filament_self_index = []
    for (let i = 1; i <= colorNums; i++) {
      filament_self_index.push(i.toString())
      filament_self_index.push(i.toString())
    }
    jsonConf.filament_self_index = filament_self_index

    return jsonConf
  }
}
export default main
